/**
 * Existing-customer Home marketing opt-in frequency (client).
 * Conservative: once per session + 14d after dismiss; UNKNOWN only.
 * Suppress Home card in the same session after the entry legal gate marketing option was shown.
 */
export const HOME_MARKETING_OPTIN_STORAGE_KEY = "asiastyle.store-v2.mkt-optin-home-v1";
export const HOME_MARKETING_OPTIN_COOLDOWN_MS = 14 * 86_400_000;
export const HOME_MARKETING_COPY_VERSION_CLIENT = "HOME_MARKETING_OPTIN_R1_20260820";

export type HomeMarketingOptInMemory = {
  dismissed_at: string | null;
  granted_at: string | null;
  last_shown_session: string | null;
  /** Session where entry ConsentGate marketing option was visible (same-session Home suppress). */
  entry_option_seen_session: string | null;
};

export function readHomeMarketingOptInMemory(): HomeMarketingOptInMemory {
  try {
    const raw = window.localStorage.getItem(HOME_MARKETING_OPTIN_STORAGE_KEY);
    if (!raw) {
      return {
        dismissed_at: null,
        granted_at: null,
        last_shown_session: null,
        entry_option_seen_session: null,
      };
    }
    const p = JSON.parse(raw) as Partial<HomeMarketingOptInMemory>;
    return {
      dismissed_at: typeof p.dismissed_at === "string" ? p.dismissed_at : null,
      granted_at: typeof p.granted_at === "string" ? p.granted_at : null,
      last_shown_session: typeof p.last_shown_session === "string" ? p.last_shown_session : null,
      entry_option_seen_session:
        typeof p.entry_option_seen_session === "string" ? p.entry_option_seen_session : null,
    };
  } catch {
    return {
      dismissed_at: null,
      granted_at: null,
      last_shown_session: null,
      entry_option_seen_session: null,
    };
  }
}

export function writeHomeMarketingOptInMemory(next: HomeMarketingOptInMemory): void {
  try {
    window.localStorage.setItem(HOME_MARKETING_OPTIN_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

/** Call when entry ConsentGate shows the optional marketing control. */
export function markEntryMarketingOptionSeen(sessionKey: string): void {
  const mem = readHomeMarketingOptInMemory();
  writeHomeMarketingOptInMemory({ ...mem, entry_option_seen_session: sessionKey });
}

export function shouldShowHomeMarketingOptIn(input: {
  permissionState: string;
  sessionKey: string;
  nowMs?: number;
}): boolean {
  const state = String(input.permissionState ?? "").trim().toUpperCase();
  if (state !== "UNKNOWN") return false;
  const mem = readHomeMarketingOptInMemory();
  if (mem.granted_at) return false;
  if (mem.last_shown_session === input.sessionKey) return false;
  if (mem.entry_option_seen_session === input.sessionKey) return false;
  if (mem.dismissed_at) {
    const t = Date.parse(mem.dismissed_at);
    const now = input.nowMs ?? Date.now();
    if (Number.isFinite(t) && now - t < HOME_MARKETING_OPTIN_COOLDOWN_MS) return false;
  }
  return true;
}
